<template>
  <!-- Minimal Toast Notification -->
  <Teleport to="body">
    <Transition name="slide-fade">
      <div
        v-if="showToast"
        class="fixed bottom-4 right-4 z-50 max-w-sm"
      >
        <div
          class="bg-orange-50 rounded-lg shadow-xl border-l-4 border-orange-500 p-4 flex items-start space-x-3"
        >
          <!-- Warning Icon -->
          <div class="flex-shrink-0">
            <div class="bg-orange-100 rounded-full p-2">
              <svg
                class="w-5 h-5 text-orange-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
          </div>

          <!-- Content -->
          <div class="flex-1">
            <p class="text-sm font-semibold text-gray-900">
              Entegrasyon Uyarısı
            </p>
            <p class="text-sm text-gray-700 mt-1">
              {{ errorMessage }}
            </p>
            <p class="text-xs text-gray-600 mt-1">
              Asistanınız çalışmaya devam edecek.
            </p>
            
            <!-- Actions -->
            <div class="flex items-center space-x-3 mt-3">
              <button
                @click="showDetails"
                class="text-sm font-medium text-orange-600 hover:text-orange-700 transition-colors"
              >
                Detayları Gör
              </button>
              
              <button
                v-if="canIgnore"
                @click="handleIgnore"
                class="text-sm font-medium text-gray-600 hover:text-gray-700 transition-colors"
              >
                Yoksay
              </button>
              
              <button
                @click="closeToast"
                class="text-sm text-gray-500 hover:text-gray-600 ml-auto transition-colors"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Details Modal -->
    <Transition name="modal-fade">
      <div
        v-if="showDetailsModal"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        <!-- Backdrop -->
        <div
          class="fixed inset-0 bg-black bg-opacity-50"
          @click="closeDetailsModal"
        ></div>

        <!-- Modal Content -->
        <div
          class="relative bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden"
        >
          <!-- Header -->
          <div class="px-6 py-4 border-b border-gray-200">
            <div class="flex items-center justify-between">
              <h3 class="text-lg font-semibold text-gray-900">
                Entegrasyon Durumu
              </h3>
              <button
                @click="closeDetailsModal"
                class="text-gray-400 hover:text-gray-600"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <!-- Body -->
          <div class="px-6 py-4 overflow-y-auto max-h-[60vh]">
            <div v-if="errorIntegrations.length > 0">
              <p class="text-sm text-gray-600 mb-4">
                Aşağıdaki entegrasyonlarda bağlantı sorunu tespit edildi:
              </p>
              
              <ul class="space-y-2">
                <li
                  v-for="integration in errorIntegrations"
                  :key="integration.id"
                  class="flex items-center p-3 bg-red-50 rounded-lg"
                >
                  <div class="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
                  <div class="flex-1">
                    <p class="text-sm font-medium text-gray-900">
                      {{ integration.name }}
                    </p>
                    <p class="text-xs text-gray-600">
                      {{ getIntegrationType(integration) }}
                    </p>
                  </div>
                </li>
              </ul>
            </div>

            <!-- Auto Recovery Info -->
            <div class="mt-4 p-3 bg-blue-50 rounded-lg">
              <p class="text-sm text-blue-800">
                <span class="font-medium">Otomatik Kontrol:</span>
                Sistem her 30 saniyede bir bağlantıları kontrol eder.
              </p>
            </div>
          </div>

          <!-- Footer -->
          <div class="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div class="flex justify-end space-x-3">
              <button
                @click="retryNow"
                class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Şimdi Kontrol Et
              </button>
              <button
                @click="closeDetailsModal"
                class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useHealthCheckStore } from '../stores/healthCheck';
import { useAuthStore } from '../stores/auth';
import { getTypeName } from '../views/integrations/types/integration';

const healthStore = useHealthCheckStore();
const authStore = useAuthStore();


// Local state
const showToast = ref(false);
const showDetailsModal = ref(false);
const toastTimer = ref<number | null>(null);

// Computed properties
const errorMessage = computed(() => {
  const count = healthStore.issueCount;
  if (count === 0) return '';
  return `${count} entegrasyonda bağlantı sorunu var.`;
});

const canIgnore = computed(() => healthStore.canIgnore);
const errorIntegrations = computed(() => healthStore.errorIntegrations);

// Methods
const showDetails = () => {
  showDetailsModal.value = true;
  closeToast();
};

const closeDetailsModal = () => {
  showDetailsModal.value = false;
};

const handleIgnore = () => {
  healthStore.ignoreWarning();
  closeToast();
};

const closeToast = () => {
  showToast.value = false;
  if (toastTimer.value) {
    clearTimeout(toastTimer.value);
    toastTimer.value = null;
  }
};

const retryNow = async () => {
  closeDetailsModal();
  await healthStore.checkHealth();
};

const getIntegrationType = (integration: any) => {
  return getTypeName(integration);
};

// Auto-hide toast after 15 seconds
const startToastTimer = () => {
  if (toastTimer.value) {
    clearTimeout(toastTimer.value);
  }
  toastTimer.value = window.setTimeout(() => {
    closeToast();
  }, 15000);
};

// Watch for health issues
watch(() => healthStore.visibleIssues, (hasIssues) => {
  if (hasIssues && authStore.isAuthenticated) {
    showToast.value = true;
    startToastTimer();
  } else {
    closeToast();
  }
});

// Watch for auth changes
watch(() => authStore.isAuthenticated, (isAuth) => {
  if (isAuth) {
    healthStore.startPeriodicCheck();
  } else {
    healthStore.reset();
    closeToast();
  }
});

// Lifecycle
onMounted(() => {
  if (authStore.isAuthenticated) {
    healthStore.startPeriodicCheck();
  }
});

onBeforeUnmount(() => {
  healthStore.stopPeriodicCheck();
  if (toastTimer.value) {
    clearTimeout(toastTimer.value);
  }
});
</script>

<style scoped>
/* Slide fade transition for toast */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

/* Modal fade transition */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .relative,
.modal-fade-leave-active .relative {
  transition: transform 0.3s ease;
}

.modal-fade-enter-from .relative {
  transform: scale(0.95);
}

.modal-fade-leave-to .relative {
  transform: scale(0.95);
}
</style>